// M5.10: condition tester for scoring rules.
//
// What: paste (or load) a sample payload on the left, build a
// condition tree on the right using the existing
// ConditionBuilder UI, see pass/fail in real time below with
// a tree-trace showing each leaf's evaluated value.
//
// Why: writing scoring rule conditions from the spec is the
// one task that requires seeing BOTH the wire-shape of the
// upstream payload AND the recursive condition DSL. Without
// this tool, the loop is:
//   1. Write condition in the scoring rule form
//   2. Save the rule
//   3. Wait for an event to score
//   4. Notice no XP awarded
//   5. Open the raw event modal to see what the payload
//      actually looked like
//   6. Edit the rule
//   7. Repeat
//
// With this tool the loop collapses to:
//   1. Paste/load a payload here
//   2. Build the condition visually
//   3. See pass/fail immediately
//   4. Copy the condition as JSON to drop into a rule
//
// The trace shows each leaf's actual value (e.g.
// `transition.to === "Done"` resolved to `transition.to = "In
// Review"` and didn't match) so the failure mode is obvious
// without re-running the engine.

import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ingestorsApi, type ConditionTraceNode } from './ingestors/api';
import type { ConditionTree } from './ingestors/types';
import { ConditionBuilder } from './ingestors/ConditionBuilder';

// Preset payloads for common event types. These are realistic
// samples that admins can compare their condition shapes
// against without having to load a real event from the DB.
// Each preset is the SAME shape the ingestor would persist
// (normalised), so the rules will read fields the same way.
const PRESETS: Record<string, { label: string; payload: Record<string, unknown> }> = {
  'jira-issue-transition-to-done': {
    label: 'Jira: ticket moved to Done',
    payload: {
      issue_type: 'Bug',
      issue_summary: 'YOLO-6517 Add missing restore range for EMEA v8',
      transition: { from: 'In Review', to: 'Done', changelog_id: '12345' },
    },
  },
  'jira-issue-transition-to-in-review': {
    label: 'Jira: ticket moved to In Review',
    payload: {
      issue_type: 'Story',
      issue_summary: 'YOLO-6098 Rebuild tenant incentive timing',
      transition: { from: 'In Progress', to: 'In Review', changelog_id: '67890' },
    },
  },
  'jira-issue-created-high-priority': {
    label: 'Jira: high-priority ticket created',
    payload: {
      issue_type: 'Bug',
      issue_summary: 'Customer can\'t see invoices',
      issue_status: 'Open',
      issue_status_category: 'new',
      issue_priority: 'High',
      issue_labels: ['yolo', 'customer-blocker'],
      changelog: [],
    },
  },
  'jira-comment-contains-engine': {
    label: 'Jira: comment contains "engine"',
    payload: {
      issue_type: 'Story',
      issue_summary: 'Refactor the engine module',
      comment_id: '98765',
      comment_body: 'I just looked at the new engine code — looks good to me. Ship it.',
    },
  },
  'github-pr-opened': {
    label: 'GitHub: PR opened',
    payload: {
      action: 'opened',
      pr_number: 18,
      pr_state: 'open',
      pr_merged: false,
      pr_merged_by: null,
      pr_merged_at: null,
      pr_draft: false,
      pr_title: 'Add retry logic to engine startup',
      pr_additions: 42,
      pr_deletions: 7,
      pr_changed_files: 3,
      pr_base_ref: 'master',
      pr_head_ref: 'worktree-x',
      pr_labels: ['enhancement'],
      repo: 'Forbury/Forbury',
    },
  },
  'github-pr-merged': {
    label: 'GitHub: PR merged',
    payload: {
      action: 'closed',
      pr_number: 18,
      pr_state: 'closed',
      pr_merged: true,
      pr_merged_by: 'gavin',
      pr_merged_at: '2026-07-13T21:48:31Z',
      pr_draft: false,
      pr_title: 'Add retry logic to engine startup',
      pr_labels: [],
      repo: 'Forbury/Forbury',
    },
  },
  'github-pr-review-changes-requested': {
    label: 'GitHub: review changes requested',
    payload: {
      review_id: 12345,
      review_state: 'changes_requested',
      pr_number: 18,
      repo: 'Forbury/Forbury',
    },
  },
};

function parsePayload(text: string): { value: Record<string, unknown>; err: string | null } {
  if (!text.trim()) return { value: {}, err: null };
  try {
    return { value: JSON.parse(text) as Record<string, unknown>, err: null };
  } catch (e) {
    return { value: {}, err: e instanceof Error ? e.message : 'invalid JSON' };
  }
}

export function ConditionTesterPage() {
  const [payloadText, setPayloadText] = useState(() =>
    JSON.stringify(PRESETS['jira-issue-transition-to-done']!.payload, null, 2),
  );
  const [conditions, setConditions] = useState<ConditionTree | null>(null);

  // Mirror the inputs into debounced versions after 500ms of
  // idle. The eval only fires on the debounced values so we
  // don't hammer the server on every keystroke.
  const [debouncedPayloadText, setDebouncedPayloadText] = useState(payloadText);
  const [debouncedConditions, setDebouncedConditions] = useState(conditions);

  // Parse the live payload on every keystroke for the inline
  // JSON-error display (cheap, no network).
  const liveParsed = useMemo(() => parsePayload(payloadText), [payloadText]);
  // Parse the debounced payload for the eval (only changes
  // every 500ms). Memoized so the effect doesn't fire on
  // each keystroke.
  const debouncedParsed = useMemo(() => parsePayload(debouncedPayloadText), [debouncedPayloadText]);

  const evalMut = useMutation<
    { result: boolean; trace: ConditionTraceNode | null },
    Error,
    { payload: Record<string, unknown>; conditions: ConditionTree | null }
  >({
    mutationFn: (body) => ingestorsApi.evalCondition(body),
  });

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedPayloadText(payloadText);
      setDebouncedConditions(conditions);
    }, 500);
    return () => window.clearTimeout(t);
  }, [payloadText, conditions]);

  // Eval when the debounced values change AND parse cleanly.
  // The debouncedParsed memo is stable for the same parsed
  // value, so this effect only fires when the actual eval
  // inputs change.
  useEffect(() => {
    if (debouncedParsed.err) return;
    if (!debouncedPayloadText.trim()) return;
    evalMut.mutate({
      payload: debouncedParsed.value,
      conditions: debouncedConditions,
    });
    // evalMut is a stable reference from React Query; the
    // explicit omit is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedParsed, debouncedPayloadText, debouncedConditions]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Condition tester</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Build a scoring rule condition visually, against a real payload shape.
          Evaluates on every edit (debounced 500ms). Copy the resulting JSON
          straight into the <strong>Scoring rules</strong> form.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* LEFT: payload pane */}
        <div className="card">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Payload</h2>
            <div className="flex items-center gap-2">
              <select
                className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs"
                defaultValue=""
                onChange={(e) => {
                  const preset = PRESETS[e.target.value];
                  if (preset) {
                    setPayloadText(JSON.stringify(preset.payload, null, 2));
                  }
                }}
              >
                <option value="">Load sample…</option>
                {Object.entries(PRESETS).map(([key, p]) => (
                  <option key={key} value={key}>{p.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => {
                  try {
                    const formatted = JSON.stringify(JSON.parse(payloadText), null, 2);
                    setPayloadText(formatted);
                  } catch {
                    // payload is broken JSON; user already
                    // sees the inline error, no need to do
                    // anything here.
                  }
                }}
                title="Pretty-print the JSON"
              >
                Format
              </button>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setPayloadText('')}
              >
                Clear
              </button>
            </div>
          </div>
          <textarea
            className="input min-h-[24rem] w-full font-mono text-xs"
            value={payloadText}
            onChange={(e) => setPayloadText(e.target.value)}
            spellCheck={false}
          />
          {liveParsed.err ? (
            <p className="mt-2 text-xs text-accent-danger" role="alert">
              JSON error: {liveParsed.err}
            </p>
          ) : null}
        </div>

        {/* RIGHT: condition pane */}
        <div className="card">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Condition</h2>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setConditions(null)}
            >
              Clear
            </button>
          </div>
          <ConditionBuilder value={conditions} onChange={setConditions} />
          {conditions ? (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-ink-muted">
                Show as JSON
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto rounded-md border border-white/5 bg-bg-elevated p-2 font-mono text-xs">
                {JSON.stringify(conditions, null, 2)}
              </pre>
            </details>
          ) : null}
        </div>
      </div>

      {/* BELOW: results */}
      <div className="card">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Result</h2>
          {evalMut.isPending ? (
            <span className="text-xs text-ink-muted">evaluating…</span>
          ) : evalMut.error ? (
            <span className="text-xs text-accent-danger">{String(evalMut.error.message)}</span>
          ) : evalMut.data ? (
            <span
              className={
                'rounded-full px-3 py-1 text-sm font-medium ' +
                (evalMut.data.result
                  ? 'bg-accent-success/15 text-accent-success'
                  : 'bg-accent-danger/15 text-accent-danger')
              }
            >
              {evalMut.data.result ? '✓ Matches' : '✕ No match'}
            </span>
          ) : null}
        </div>
        {evalMut.data?.trace ? (
          <TraceTree trace={evalMut.data.trace} depth={0} />
        ) : (
          <p className="mt-2 text-xs text-ink-dim">
            No condition to evaluate yet. Add a leaf or group on the right.
          </p>
        )}
        <RuleExportHint conditions={conditions} />
      </div>
    </div>
  );
}

// Recursive trace tree. Renders each node expanded to show
// the resolved value (for leaves) or the per-child result
// (for groups). All groups default to expanded so the admin
// sees the full picture on first glance.
function TraceTree({ trace, depth }: { trace: ConditionTraceNode; depth: number }) {
  if (trace.kind === 'leaf') {
    // The trace node's `condition` field is typed as the
    // full ConditionTree union (since the server doesn't
    // guarantee which shape a leaf's condition has). Use
    // the trace's `kind === 'leaf'` as the discriminator
    // and shape-narrow the condition via runtime checks
    // ('exists' in cond first; otherwise {field, op, value}).
    const cond = trace.condition;
    if ('exists' in cond) {
      return (
        <LeafTrace
          label={`exists ${cond.exists}`}
          actual={trace.actual}
          result={trace.result}
          depth={depth}
        />
      );
    }
    // Past this point the condition MUST be a leaf (the
    // server only emits leaves for trace.kind === 'leaf').
    // The runtime checks are belt-and-braces; TS narrows
    // via the casts below using the shape guarantees.
    const field = (cond as { field?: string }).field ?? '?';
    const op = (cond as { op?: string }).op ?? '?';
    const value = (cond as { value?: unknown }).value;
    return (
      <LeafTrace
        label={`${field} ${op} ${JSON.stringify(value)}`}
        actual={trace.actual}
        result={trace.result}
        depth={depth}
      />
    );
  }
  const groupLabel =
    trace.kind === 'all' ? 'ALL of' : trace.kind === 'any' ? 'ANY of' : 'NOT';
  return (
    <div
      className="mt-1 rounded-md border border-white/10 bg-bg-elevated/40 p-2"
      style={{ marginLeft: `${depth * 12}px` }}
    >
      <div className="flex items-center justify-between text-xs">
        <span className="text-ink-muted">{groupLabel}</span>
        <span
          className={
            'rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ' +
            (trace.result
              ? 'bg-accent-success/15 text-accent-success'
              : 'bg-accent-danger/15 text-accent-danger')
          }
        >
          {trace.result ? 'passed' : 'failed'}
        </span>
      </div>
      <div className="mt-1 space-y-1">
        {trace.kind === 'not' ? (
          trace.child ? (
            <TraceTree trace={trace.child} depth={depth + 1} />
          ) : (
            <p className="text-xs text-ink-dim">not: missing child</p>
          )
        ) : Array.isArray(trace.children) && trace.children.length > 0 ? (
          trace.children.map((c, i) => (
            <div key={i}>
              <TraceTree trace={c} depth={depth + 1} />
            </div>
          ))
        ) : trace.kind === 'all' || trace.kind === 'any' ? (
          // Empty group (no children) is a legitimate state
          // (e.g. user just clicked "Add condition" and
          // hasn't added a leaf yet). Show an empty-state
          // hint instead of "malformed trace" — the latter
          // is misleading when the trace shape IS what the
          // server intended.
          <p className="text-xs text-ink-dim">no child conditions yet</p>
        ) : (
          // Genuine mismatch: trace.kind is something we
          // don't render (e.g. a future kind the api adds).
          // Show the literal JSON so the dev can see what's
          // going on.
          <p className="text-xs text-ink-dim">
            unrecognised trace shape:{' '}
            <code className="rounded bg-bg-elevated px-1 py-0.5">
              {JSON.stringify(trace).slice(0, 200)}
            </code>
          </p>
        )}
      </div>
    </div>
  );
}

function LeafTrace({
  label,
  actual,
  result,
  depth,
}: {
  label: string;
  actual: unknown;
  result: boolean;
  depth: number;
}) {
  return (
    <div
      className="mt-1 flex items-baseline justify-between gap-2 rounded-md border border-white/10 bg-bg-elevated/40 p-2 text-xs"
      style={{ marginLeft: `${depth * 12}px` }}
    >
      <div className="min-w-0 flex-1">
        <p className="font-mono text-ink">{label}</p>
        <p className="mt-0.5 truncate font-mono text-ink-muted" title={JSON.stringify(actual)}>
          resolved to: <span className="text-ink-dim">{formatValue(actual)}</span>
        </p>
      </div>
      <span
        className={
          'flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ' +
          (result
            ? 'bg-accent-success/15 text-accent-success'
            : 'bg-accent-danger/15 text-accent-danger')
        }
      >
        {result ? 'passed' : 'failed'}
      </span>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === undefined) return 'undefined';
  if (v === null) return 'null';
  if (typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function RuleExportHint({ conditions }: { conditions: ConditionTree | null }) {
  const [copied, setCopied] = useState(false);
  if (!conditions) return null;
  const json = JSON.stringify(conditions, null, 2);
  return (
    <div className="mt-4 rounded-md border border-white/5 bg-bg-elevated p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-medium text-ink-muted">JSON to paste into a scoring rule</p>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(json);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            } catch {
              // non-secure context; user can still select
              // and copy from the <pre> manually.
            }
          }}
        >
          {copied ? 'Copied!' : 'Copy JSON'}
        </button>
      </div>
      <pre className="max-h-64 overflow-auto rounded-md border border-white/5 bg-bg p-2 font-mono text-xs">
{json}
      </pre>
    </div>
  );
}
